import React, { useState } from 'react';
import Section, { SectionBody, SectionBodyText } from '../components/Section';
import BackToTop from '../components/BackToTop';
import { SectionImage } from '../components/Section';
import LocationLink from '../components/LocationLink';
import PhotoCaption from '../components/PhotoCaption';

interface HomeProps {
    theme: 'light' | 'dark';
}

const Home: React.FC<HomeProps> = ({ theme }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [heroImageLoaded, setHeroImageLoaded] = useState(false);

    return (
        <div className={`max-w-[652px] ${theme === 'light' ? 'bg-white text-black' : 'text-white'}`}>
            <Section id="home" title={undefined}>
                <SectionBody>
                    <SectionBodyText>
                        I'm a designer, founder, photographer, and creative director living in New York City since 2011.
                    </SectionBodyText>
                    <SectionBodyText>
                        I'm co-founder and CEO of <a href="https://supersetapp.com" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">Superset</a>, an <a href="https://insider.fitt.co/press-release/superset-launches-ai-in-superset-sheets-ushering-in-a-new-era-of-intelligent-programming/" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">AI-native</a> platform for gyms, health clubs, enterprises, and coaching teams. We serve 1,000+ businesses across 24 countries and power more than 50,000 workouts each month.
                    </SectionBodyText>
                    <SectionBodyText>
                        My work is driven by new media and emerging paradigms that blend art, software, and commerce into scalable, human-centered experiences.
                    </SectionBodyText>
                    <div className="my-6">
                        <div className="relative">
                            {!heroImageLoaded && (
                                <div className="absolute inset-0 bg-neutral-800 bg-opacity-90 flex items-center justify-center rounded-lg">
                                    <span className="text-gray-500">Loading…</span>
                                </div>
                            )}
                            <div className="w-full h-[615px] lg:aspect-auto lg:h-[620px]">
                                <img
                                    src="/assets/1740866357585.jpeg"
                                    alt="LinkedIn Profile"
                                    className="w-full h-full object-cover rounded-lg"
                                    onLoad={() => setHeroImageLoaded(true)}
                                />
                                <PhotoCaption
                                    location="Superset HQ — Soho, New York City"
                                    coordinates="40.7233,-74.0030"
                                />
                            </div>
                        </div>
                    </div>
                </SectionBody>
            </Section>

            <Section id="2020" title="Jan 2020">
                <SectionBody>
                    <SectionBodyText>
                        After 10 years in NYC tech, I went full time on <a href="https://supersetapp.com" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">Superset</a>. We've grown the business past $1M ARR with 140% net revenue retention, supporting 1,000+ gyms and coaching teams globally.
                    </SectionBodyText>
                </SectionBody>
            </Section>

            <Section id="2019" title="Dec 2019">
                <SectionBody>
                    <SectionBodyText>
                        I wrapped a major project with Spotify, helping design software products for artists and labels. Due to NDA, I cannot show any work, but please view my <a href="https://www.instagram.com/p/B6Go1MsJojG/?img_index=1" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">Instagram post</a> or <a href="mailto:taylor@taylorpemberton.com" className="text-lime-500 hover:text-lime-400 transition-colors">email me</a> to learn more.
                    </SectionBodyText>
                </SectionBody>
            </Section>

            <Section id="2018" title="May 2018">
                <SectionBody>
                    <SectionBodyText>
                        After a stint in LA and Chicago, I moved back to NYC 🗽
                    </SectionBodyText>
                    <SectionBodyText>
                        <a href="https://pmbr.tn" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">Pemberton</a>, my design consultancy, is accepting projects on a very limited basis. If you'd like to discuss new projects, opportunities, or partnerships, please send me a note at <a href="mailto:taylor@taylorpemberton.com" className="text-lime-500 hover:text-lime-400 transition-colors">taylor@taylorpemberton.com</a>.
                    </SectionBodyText>
                    <div className="relative">
                        {!imageLoaded && (
                            <div className="absolute inset-0 bg-gray-100 bg-opacity-90 flex items-center justify-center w-full h-full rounded-lg">
                                <span className="text-gray-500">Loading…</span>
                            </div>
                        )}
                        <img
                            src="/assets/keyboard-bling.jpg"
                            alt="Keyboard bling"
                            className={`w-full h-auto rounded-lg my-6 ${imageLoaded ? 'block' : 'invisible'}`}
                            loading="lazy"
                            onLoad={() => setImageLoaded(true)}
                        />
                    </div>
                </SectionBody>
            </Section>

            <Section id="2015" title="Mar 2015">
                <SectionBody>
                    <SectionBodyText>
                        This was the start of a 15-month stretch of solo travel and photography across Peru, Bolivia, China, Thailand, Laos, Cambodia, Vietnam, Myanmar, South Korea, North Korea, the Philippines, Singapore, Brunei, Indonesia, Malaysia, Cuba, Colombia, Mexico, Sweden, and Germany. The outcome is an ongoing body of work called Contrateur, where I retroactively post excerpts and stories.
                    </SectionBodyText>
                    <SectionImage
                        src="/assets/1500x500.jpeg"
                        alt="Travel journey"
                        className="rounded-lg"
                        caption={
                            <LocationLink 
                                location="Yading Nature Reserve, China (Tibetan Prefecture)" 
                                coordinates="28.4372,100.3258" 
                            />
                        }
                    />
                    <SectionBodyText>
                        My work was featured on <a href="https://www.bbc.com/news/newsbeat-34021128" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">BBC</a>, Huffington Post, <a href="https://www.dazeddigital.com/photography/article/26043/1/this-photographer-is-instagramming-from-inside-north-korea" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">DAZED Magazine</a>, and <a href="https://qz.com/485636/humans-of-north-korea-an-ordinary-american-tourists-photos-from-inside-the-hermit-kingdom" className="text-lime-500 hover:text-lime-400 transition-colors" target="_blank" rel="noopener noreferrer">Quartz</a>.
                    </SectionBodyText>
                </SectionBody>
            </Section>

            <BackToTop theme={theme} />
        </div>
    );
};

export default Home; 